import React, { PropTypes, Component } from 'react';
import Helmet from 'react-helmet';
import InContentAd from 'common/ads/InContentAd';
import classNames from 'classnames';
import {
    BucketLayout,
    PageType,
    BucketRatio,
    BucketRatioCols,
    LayoutThresholds,
    LayoutVariant,
    BreakPoints
} from 'enums';
import WatchPropTypes from 'common/PropTypes';
import SearchBucketsHeader from 'common/page/search/SearchBucketsHeader';
import BucketHeaderContainer from '@alias/BucketHeaderContainer';
import CollectionBucketHeader from './CollectionBucketHeader';
import Tile from './Tile';

const getExpandableThresholds = (maxToDisplay = 100) => (
    {
        [BucketLayout.DEFAULT_EXPANDABLE]: [4, maxToDisplay],
        [BucketLayout.LARGE_EXPANDABLE]: [8, maxToDisplay]
    }
);

const collectionRowClassMap = {
    [BucketLayout.FiveTwo]: 'dtc-watch__row--promos',
    [BucketLayout.TwoThree]: 'dtc-watch__row--films',
    [BucketLayout.FourThree]: 'dtc-watch__row--shows'
};

const dtcItemLayouts = [
    BucketLayout.FiveTwo,
    BucketLayout.TwoThree,
    BucketLayout.FourThree
];

const featureLayouts = [
    BucketLayout['feature-left'],
    BucketLayout['feature-right'],
    BucketLayout.TAPESTRY_LEFT,
    BucketLayout.TAPESTRY_RIGHT
];

const featureLeftLayout = [
    BucketLayout['feature-left'],
    BucketLayout.TAPESTRY_LEFT
];

const featureRightLayout = [
    BucketLayout['feature-right'],
    BucketLayout.TAPESTRY_RIGHT
];

const breakPointMqMap = {
    mobile: '@media screen and (max-width: 597px)',
    'mobile-lg': '@media screen and (min-width: 598px) and (max-width: 767px)',
    tablet: '@media screen and (min-width: 768px) and (max-width: 1023px)',
    desktop: '@media screen and (min-width: 1024px) and (max-width: 1259px)',
    'desktop-lg': '@media screen and (min-width: 1260px)'
};

const ratioBreakPointMap = {
    [BucketRatio.SixteenNine]: ['tablet', 'desktop', 'desktop-lg'],
    [BucketRatio.FourThree]: ['mobile', 'mobile-lg', 'tablet', 'desktop', 'desktop-lg'],
    [BucketRatio.FiveTwo]: ['tablet', 'desktop', 'desktop-lg'],
    [BucketRatio.TwoThree]: ['mobile', 'mobile-lg', 'tablet', 'desktop', 'desktop-lg']
};

/**
 * Calculates the dimension of bucket
 *
 * @param {string} ratio - tile ratio
 * @param {string} mq - media query break point
 * @param {number} contentLength - number of contents in bucket
 * @param {boolean} calculateRows - a flag to indicate whether we retrieve the maximum rows to display
 * @return {object} - dimension of bucket
 */
export function getBucketDimension(
    ratio = BucketRatio.SixteenNine,
    mq = BreakPoints.DESKTOP_LARGE,
    contentLength = 0,
    calculateRows = false
) {
    const columns = BucketRatioCols[mq][ratio];
    let rows;
    if (columns) {
        if (calculateRows) {
            rows = Math.max(1, Math.floor(contentLength / columns));
        } else {
            rows = contentLength >= 2 * columns ? 2 : 1;
        }
    }
    return { rows, columns };
}

export function generateBucketCss(row, ratio = BucketRatio.SixteenNine, pageType) {
    // NOTE: For catalog template pages (i.e. /watch/series) we do not want to hide tiles via css
    // This prevents the bucketCssCache from saving upon client side route transitions.
    if (pageType === PageType.CATALOG) {
        return '';
    }

    const breakPoints = ratioBreakPointMap[ratio];
    let css = '';
    /* eslint-disable max-len */
    breakPoints.forEach(key => {
        const mediaFeatures = breakPointMqMap[key];
        if (!mediaFeatures) {
            return;
        }

        const col = BucketRatioCols[key][ratio];
        if (ratio === BucketRatio.SixteenNine) {
            css += `
                ${mediaFeatures} {
                    .watch-home .collection--watch .collection_row--${row} article.collection_item--one:nth-of-type(1n+${(col * row) + 1}){
                        display:none;
                    }
                }
            `;
        } else {
            const layoutKey = Object.keys(BucketRatio).filter(ratioKey => BucketRatio[ratioKey] === ratio)[0];
            const layoutType = BucketLayout[layoutKey];
            css += `
                ${mediaFeatures} {
                    .watch-home .${collectionRowClassMap[layoutType]}.dtc-watch__row--${row} .dtc-watch__item:nth-of-type(1n+${(col * row) + 1}){
                        display:none;
                    }
                }
            `;
        }
    });

    return css;
}


class Bucket extends Component {
    constructor(props) {
        super(props);
        this.getImageFormat = this.getImageFormat.bind(this);
        this.getLayoutType = this.getLayoutType.bind(this);
        this.getBucketLink = this.getBucketLink.bind(this);
        this.isTileFeatured = this.isTileFeatured.bind(this);
        this.isPagePreview = this.isPagePreview.bind(this);
        this.shouldShowAllTiles = this.shouldShowAllTiles.bind(this);
        this.showMoreClickHandler = this.showMoreClickHandler.bind(this);
        this.state = {
            expanded: false
        };
    }

    /**
     * getImageFormat - Returns the proper image format that depends on the medata. Secondary
     * fallback value is derived from the contents.imageFormat. Final fallback is '16x9'
     *
     * @return {String}
     */
    getImageFormat() {
        const {
            contents,
            metadata: { imageFormat: imageFormatBucket }
        } = this.props;
        const { imageFormat: imageFormatContent } = contents[0] || {};

        return (imageFormatBucket || imageFormatContent || BucketRatio.SixteenNine);
    }

    /**
     * getLayoutType - Returns the type of layout to be used for the bucket. Utilizes the pageType
     * and imageFormat to obtain the proper value. It can be overwritten by the prop.layoutType.
     *
     * @param {string} pageType
     * @param {string} imageFormat
     *
     * @return {string}
     */
    getLayoutType(pageType, imageFormat = '') {
        const { layoutOverride, layout } = this.props;
        const apiLayout = layout || '';
        let finalLayout;

        if (apiLayout !== '' && BucketLayout[apiLayout]) {
            // API match found (i.e. feature-left, feature-right)
            finalLayout = BucketLayout[apiLayout];
        } else if (imageFormat === BucketRatio.TwoThree) {
            finalLayout = BucketLayout.TwoThree;
        } else if (imageFormat === BucketRatio.FourThree) {
            finalLayout = BucketLayout.FourThree;
        } else if (imageFormat === BucketRatio.FiveTwo) {
            finalLayout = BucketLayout.FiveTwo;
        } else if (imageFormat === BucketRatio.SixteenNine) {
            finalLayout = BucketLayout.SixteenNine;
        } else {
            finalLayout = (pageType === PageType.COLLECTION) ?
                BucketLayout.COLLECTION : BucketLayout.DEFAULT;
        }

        // Override all predefined layouts; dependent on `this.props.layoutOverride`
        if (layoutOverride) {
            finalLayout = layoutOverride;
        }

        return finalLayout;
    }

    /**
     * getBucketLink - Returns the proper bucket link to be used based on the following conditions:
     * 1) If the page is on preview mode, it uses the props.name to generate the bucket link.
     * 2) If the props.link is defined and shouldShowAllTiles() is false, it uses the props.link value
     * @return {string} - Bucket URL
     */
    getBucketLink() {
        const { link, name } = this.props;
        let bucketLink;
        // TODO: Fix this when the correct url is coming back from the page api.
        // Also note the condition in !this.shouldShowAllTiles() below
        if (this.isPagePreview()) {
            bucketLink = `/${name.toLowerCase()}`;
        } else if (link && !this.shouldShowAllTiles()) {
            bucketLink = link;
        }

        return bucketLink;
    }

    /**
     * isPagePreview - Returns a boolean flag value that determine if the page is a preview page
     * based on the existence of `page_preview` in the tags prop.
     *
     * @return {boolean}
     */
    isPagePreview() {
        const { tags } = this.props;
        return tags && (tags.indexOf('page_preview') !== -1);
    }

    /**
     * isTileFeatured - Returns a boolean flag value that verifies the existence of the contents
     * in addition to matching the index and layoutType.
     *
     * @param {number} index - current index of the array for the given tile
     * @param {string} layoutType - the type of layout to determine if the tile should be featured
     *
     * @return {boolean}
     */
    isTileFeatured(index, layoutType, featureLayoutsList = []) {
        const { contents } = this.props;

        return (contents && index === 0 && featureLayoutsList.indexOf(layoutType) !== -1);
    }

    /**
     * shouldShowAllTiles - Returns a boolean flag that only returns true if the pageType is `COLLECTION`
     * or if the layoutType is 1) TwoThree or 2) FourThree and the hideHeaderLink boolean is true.
     *
     * @return {boolean}
     */
    shouldShowAllTiles() {
        const { hideHeaderLink, pageType } = this.props;
        const layoutType = this.getLayoutType(pageType, this.getImageFormat());

        return pageType === PageType.COLLECTION ||
            (
                (layoutType === BucketLayout.TwoThree || layoutType === BucketLayout.FourThree) &&
                hideHeaderLink
            );
    }

    showMoreClickHandler() {
        this.setState({ expanded: !this.state.expanded });
    }

    render() {
        const {
            bucketId,
            contents,
            groupId,
            iconHref,
            metadata: { totalCount, displayCount },
            name,
            pageType,
            tags,
            tier
        } = this.props;
        const imageFormat = this.getImageFormat();
        const layoutType = this.getLayoutType(pageType, imageFormat);
        const layoutTypeHasDefinedShreshhold = {}.hasOwnProperty.call(LayoutThresholds, layoutType);
        const bucketLink = this.getBucketLink();
        const isFeaturedLeftLayout = (featureLeftLayout.indexOf(layoutType) !== -1);
        const isFeaturedRightLayout = (featureRightLayout.indexOf(layoutType) !== -1);
        // There are 2 reasons to distinguish between the legacy layout and ones introduced for DTC
        // 1. tiles in DTC layout is not playable, but navigable (5x2, 4x3 and 2x3)
        // 2. css class works differently
        const layoutVariant = dtcItemLayouts.indexOf(layoutType) !== -1 ? LayoutVariant.DTC : LayoutVariant.COLLECTION;
        const isDTCLayout = layoutVariant === LayoutVariant.DTC;
        const isTileNavigable = isDTCLayout;
        const displaySize = !this.isPagePreview() && !isTileNavigable;
        let layoutThreshold;
        let rows;

        if (layoutTypeHasDefinedShreshhold) { // use the static threshold for the `layoutType` when defined
            layoutThreshold = LayoutThresholds[layoutType];
        } else {
            // by default, `displayCount` matches with `totalCount` when not entered or
            // set larger than `totalCount` in admin console, use the default layout logic which is 1 row and
            // 2 when tiles are enough to fill 2 rows on non show/film pages.
            // Otherwise, calculate maximum rows that can fill up with tiles.
            const calculateRows = (displayCount < totalCount);
            // retrieve the maximum `layoutThreshold` using the largest break point, then hide tiles accordingly with css
            // for the smaller break point, that's more performant than re-rendering the exact tiles per break points.
            const {
                rows: bucketRows,
                columns
            } = getBucketDimension(imageFormat, 'desktop-lg', contents.length, calculateRows);

            if (bucketRows && !this.shouldShowAllTiles()) {
                rows = bucketRows;
                // For catalog template pages such as show/film pages we want to show all content only when the `see all` link
                // is not defined. Threshold should be calculated for all other page types
                if (pageType === PageType.CATALOG) {
                    layoutThreshold = bucketLink ? (bucketRows * columns) : undefined;
                } else {
                    layoutThreshold = (bucketRows * columns);
                }
            }
        }

         // Applying `*--showAll` class overrides the tile css to always appear on all break points
        const collectionRowClassName = classNames(
            collectionRowClassMap[layoutType],
            layoutType === BucketLayout.SixteenNine && rows && `collection_row--${rows}`,
            isDTCLayout && rows && `dtc-watch__row--${rows}`,
            {
                collection_row: !isDTCLayout,
                'dtc-watch__row': isDTCLayout,
                'dtc-watch__row--showAll': isDTCLayout && this.shouldShowAllTiles(),
                'collection_row--collections': (pageType === PageType.COLLECTION),
                'collection_row--showAll': (pageType === PageType.COLLECTION)
            }
        );

        // Add classes based on the layoutType
        const collectionClassName = classNames('collection', {
            'collection--watch': (featureLayouts.indexOf(layoutType) === -1 && !isDTCLayout),
            'collection--featured-left': isFeaturedLeftLayout,
            'collection--featured-right': isFeaturedRightLayout
        });

        // Check tags to determine if bucket should be expandable
        let footer;
        const expandThresholds = getExpandableThresholds(contents.length);
        const layoutTags = tags.filter((tag) => Object.keys(expandThresholds).indexOf(tag) !== -1);

        if (layoutTags.length > 0) {
            const { expanded } = this.state;
            const layoutTag = layoutTags[0];
            const minimumToDisplay = expandThresholds[layoutTag][0];
            const maximumToDisplay = expandThresholds[layoutTag][1];
            layoutThreshold = expanded ? maximumToDisplay : minimumToDisplay;

            if (contents.length > minimumToDisplay) {
                const text = expanded ? 'Show Fewer' : 'Show More';
                const footerClass = classNames('showMore__link', 'icon-font-before', { active: expanded });
                footer = (
                    <footer className="showMore">
                        <a onClick={this.showMoreClickHandler} className={footerClass}>
                            <span>{text}</span>
                        </a>
                    </footer>
                );
            }
        }

        // `placement` is used for `playable` HOC
        let placement = `home-${name}-grid`;
        let header = (
            <BucketHeaderContainer
                className={'collection_header'}
                name={name}
                placement={placement}
                tier={tier}
                size={totalCount}
                link={bucketLink}
                displaySize={displaySize}
                isDTCLayout={isDTCLayout}
            />
        );

        switch (pageType) {
            case PageType.COLLECTION:
                header = <CollectionBucketHeader name={name} iconHref={iconHref} tags={tags} />;
                placement = `collections-${name}-grid`;
                break;
            case PageType.FEATURES:
                placement = `features-${name}-grid`;
                break;
            case PageType.SEARCH:
                placement = `search-${this.props.name}-grid`;
                header = <SearchBucketsHeader name={this.props.name} logoUrl={this.props.logoUrl} tags={this.props.tags} />;
                break;
            default:
                break;
        }

        const collectionContents = layoutThreshold ? contents.slice(0, layoutThreshold) : contents;
        /* eslint-disable */
        const collectionRowChildren = collectionContents.map((content, index) => {
            return (
                <Tile
                    bucketId={bucketId}
                    content={content}
                    groupId={groupId}
                    featured={this.isTileFeatured(index, layoutType, featureLayouts)}
                    key={`tile-${bucketId}-${index}:${content.id}:`}
                    layoutType={layoutType}
                    isNavigable={isTileNavigable}
                    placement={placement}
                    bucketName={name}
                    tier={tier}
                />
            );
        });
        /* eslint-enable */

        // TODO: is there a better way to do this?
        if (pageType === PageType.COLLECTION) {
            collectionRowChildren.splice(6, 0,
                <InContentAd
                    key="collection_row_in_content_ad"
                    include={['s']}
                    adClassName="ad-slot-incontent2"
                    refreshWhenVisible
                />
            );
        }

        let bucket = null;
        if (collectionRowChildren && collectionRowChildren.length > 0) {
            // creates media query css for given ratio(layout) and # of rows on demand
            const bucketCss = imageFormat && rows && generateBucketCss(rows, imageFormat, pageType);
            bucket = (
                <div className={collectionClassName}>
                    {bucketCss && (
                        <Helmet
                            style={[{
                                cssText: bucketCss
                            }]}
                        />
                    )}
                    {header}
                    <div className={collectionRowClassName}>
                        {collectionRowChildren}
                    </div>
                    {footer}
                </div>
            );
        }

        return bucket;
    }
}

Bucket.propTypes = {
    bucketId: PropTypes.number,
    contents: PropTypes.arrayOf(WatchPropTypes.content),
    groupId: PropTypes.number,
    iconHref: PropTypes.string,
    layout: PropTypes.string,
    layoutOverride: PropTypes.string,
    link: PropTypes.string,
    logoUrl: PropTypes.string,
    metadata: PropTypes.object,
    name: PropTypes.string,
    pageType: PropTypes.string,
    tags: PropTypes.array,
    tier: PropTypes.number,
    hideHeaderLink: PropTypes.bool
};

Bucket.defaultProps = {
    contents: [],
    metadata: {},
    name: '',
    tags: []
};

export default Bucket;



// WEBPACK FOOTER //
// ./app/common/page/buckets/Bucket.js